import { CSSProperties, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { Stack } from "capitalrx-components-2.0/lib/layout";
import classNames from "classnames";

import { Button, Buttons } from "../Button";
import { DataId } from "../DataId";
import { DataRow } from "../DataRow";
import { BaseColumnProps } from "./BaseColumnProps";
import { ColumnElementConstructor } from "./ColumnElementConstructor";
import { RowChildContentProvider } from "./RowChildContentProvider";
import { RowClickHandler } from "./RowClickHandler";
import { TableCellTail } from "./TableCellTail";
import { TableLayoutContext } from "./TableLayoutContext";
import { TableLoader } from "./TableLoader";
import { TableRowContext } from "./TableRowContext";
import { useTableStyles } from "./TableStylesContext";

export interface ColumnWidthLookupTable {
  readonly [key: string]: number;
}

/** The `TableBody` component properties. */
export interface TableBodyProps<TRow extends object> {
  /** The table rows. */
  readonly rows?: ReadonlyArray<DataRow<TRow>>;

  /** The set of expanded IDs. */
  readonly expandedIdSet: ReadonlySet<DataId>;

  /** Indicates optional child content to present for each row. */
  readonly rowChildContent?: RowChildContentProvider<TRow>;

  /** Called when the row is clicked. */
  readonly onRowClick?: RowClickHandler;
}

/**
 * Renders a set of cells.
 * @param columns The set of columns to render.
 * @param offsetProvider The optional column offset provider.
 * @returns The set of cell elements.
 */
function renderCells(columns: ReadonlyArray<ReactElement<BaseColumnProps>>, offsetProvider?: (index: number) => number) {
  return columns.map((column, index) => {
    const { props, type } = column;
    const { name } = props;
    let className = props.className;
    const headerAlignment = (type as ColumnElementConstructor<unknown>).headerAlignment;

    const align: BaseColumnProps["align"] = props.align ?? headerAlignment ?? "left";

    if (typeof type !== "string") {
      className = classNames(className, (type as ColumnElementConstructor<unknown>).className);
    }
    const transitionLeft = index === 0;
    const transitionRight = index === columns.length - 1;

    return (
      <TableBody.Row.Cell
        {...props}
        className={className}
        key={name}
        transitionLeft={transitionLeft}
        transitionRight={transitionRight}
        position={offsetProvider ? offsetProvider(index) : undefined}
        align={align}
      >
        {column}
      </TableBody.Row.Cell>
    );
  });
}

/** The `TableContentRow` component properties. */
interface TableContentRowProps {
  /** Indicates whether the content is expanded. */
  readonly expanded: boolean;

  /** Indicates whether the content is an even row. */
  readonly even: boolean;

  /** Specifies the renderable width of the content. */
  readonly width: number;

  /** The padding of the content row. If not specified defaults to `"normal"`. */
  readonly padding?: "small" | "normal";

  /** The component children. */
  readonly children?: ReactNode;
}

/** The content row component. */
const TableContentRow = (props: TableContentRowProps) => {
  const { expanded, even, padding = "normal", width, children } = props;
  const { styles } = useTableStyles();

  return (
    <tr className={styles.TableContentRow}>
      <td className={styles.TableContentCell} data-even={even} data-expanded={expanded}>
        <div className={styles.TableStickyContent} style={{ width: `${width}px`, height: expanded ? "auto" : "0px" }}>
          <div className={classNames(styles.TableContentCellPadding, padding === "small" ? "padding-small" : undefined)}>{children}</div>
        </div>
      </td>
    </tr>
  );
};

/** The `TableChildRows` component properties. */
interface TableChildRowsProps<TRow extends object> {
  /** The the rows. */
  readonly rows: ReadonlyArray<DataRow<TRow>>;

  /** Indicates whether the content is expanded. */
  readonly expanded: boolean;

  /** Indicates whether the child rows should display as even or odd. */
  readonly even: boolean;

  /** Called when the row is clicked. */
  readonly onRowClick?: RowClickHandler;
}

/** Renders the table child rows. */
const TableChildRows = <TRow extends object>(props: TableChildRowsProps<TRow>) => {
  const { rows, even, expanded, onRowClick } = props;

  if (!expanded) return null;

  return (
    <>
      {rows.map((row, index) => (
        <TableBody.Row
          key={row.id}
          row={row}
          expanded={false}
          first={index === 0}
          even={even}
          last={index === rows.length - 1}
          subRow={true}
          onClick={onRowClick}
        />
      ))}
    </>
  );
};

/** The table body component. */
export const TableBody = <TRow extends object>(props: TableBodyProps<TRow>) => {
  const { rows = [], expandedIdSet, rowChildContent, onRowClick } = props;
  const { styles } = useTableStyles();

  return (
    <tbody className={styles.TableBody}>
      {rows.map((row, index) => (
        <TableBody.Row
          key={row.id}
          row={row}
          expanded={expandedIdSet.has(row.id)}
          first={index === 0}
          even={index % 2 !== 0}
          last={index === rows.length - 1}
          subRow={false}
          childContent={rowChildContent}
          onClick={onRowClick}
        />
      ))}
    </tbody>
  );
};

/** The `TableBody.Row` component properties. */
interface TableBodyRowProps<TRow extends object> {
  /** Indicates whether the row should render as a sub-row. */
  readonly subRow: boolean;

  /** The row */
  readonly row: DataRow<TRow>;

  /** Indicates whether the row is the first row in a particular set. */
  readonly first: boolean;

  /** Indicates whether the row is the last row in a particular set. */
  readonly last: boolean;

  /** Indicates whether the row is even or not. */
  readonly even: boolean;

  /** Indicates whether the row is expanded. */
  readonly expanded: boolean;

  /** Indicates optional child content to present for each row. */
  readonly childContent?: RowChildContentProvider<TRow>;

  /** Called when the row is clicked. */
  readonly onClick?: RowClickHandler;
}

const Row = <TRow extends object>(props: TableBodyRowProps<TRow>) => {
  const { subRow, row, first, last, even, expanded, childContent, onClick } = props;
  const { id } = row;

  const { leftColumns, middleColumns, rightColumns, leftOffsets, rightOffsets, viewportWidth } = useContext(TableLayoutContext);
  const { styles } = useTableStyles();

  const className = classNames(styles.TableRow, "table-row", { first, last, "child-row": subRow, interactive: !!onClick });

  const [rowContent, setRowContent] = useState<ReactNode | ReadonlyArray<DataRow<TRow>> | null>(null);
  const [loading, setLoading] = useState<boolean | "error">(false);
  const [showNoRowsMessage, setShowNoRowsMessage] = useState(false);

  const currentLoadProcessRef = useRef<(() => void) | null>(null);

  const doLoadProcess = useCallback(() => {
    // if we have a dangling process and it's being called again, just kill the old one, this specifically handles the refresh case.
    if (currentLoadProcessRef.current) {
      currentLoadProcessRef.current();
      currentLoadProcessRef.current = null;
    }

    let cancelled = false;

    async function load() {
      const result = childContent?.(row, expanded);

      if (!result) {
        setRowContent(null);
        return;
      }

      if (result instanceof Promise) {
        setLoading(true);

        try {
          const rows = await result;
          if (cancelled) return;
          setRowContent(rows);
          setLoading(false);
        } catch {
          if (cancelled) return;
          setLoading("error");
        }

        return;
      }

      setRowContent(result);
    }

    async function initialize() {
      await load();
      currentLoadProcessRef.current = null;
    }

    initialize();

    return (currentLoadProcessRef.current = () => {
      cancelled = true;
      currentLoadProcessRef.current = null;
    });
  }, [expanded, childContent]);

  useEffect(() => doLoadProcess(), [doLoadProcess]);

  const clickHandler = useCallback(() => {
    onClick?.(id);
  }, [id, onClick]);

  const showChildRows = Array.isArray(rowContent) && rowContent.length > 0;
  const showNoRowsContent = Array.isArray(rowContent) && rowContent.length === 0;
  const showContent = !Array.isArray(rowContent) && !!rowContent;

  useEffect(() => {
    let cancelled = false;

    if (showNoRowsContent && expanded) {
      // delay the message for a single frame so if we have loading we don't "pop" the message first to the display.
      requestAnimationFrame(() => {
        if (cancelled) return;
        setShowNoRowsMessage(true);
      });
    } else setShowNoRowsMessage(false);

    return () => {
      cancelled = true;
    };
  }, [showNoRowsContent, expanded]);

  return (
    <TableRowContext.Provider key={id} value={{ row, hasChildContent: !!rowContent, isSubRow: subRow }}>
      <tr className={className} onClick={clickHandler} data-even={even} data-first={first} data-last={last} tabIndex={onClick ? 0 : -1}>
        {renderCells(leftColumns, (index) => leftOffsets[index])}
        {renderCells(middleColumns)}
        {renderCells(rightColumns, (index) => rightOffsets[rightOffsets.length - (index + 1)])}
        <TableCellTail />
      </tr>
      {loading === true && (
        <TableContentRow even={even} width={viewportWidth} expanded={expanded} padding="small">
          <Stack alignment="center">
            <TableLoader />
          </Stack>
        </TableContentRow>
      )}
      {loading === "error" && (
        <TableContentRow even={even} width={viewportWidth} expanded={expanded} padding="small">
          <Stack alignment="center" spacing="mini">
            <span>Error loading</span>
            <Button appearance={Buttons.Functional.Tertiary.Small.Unpadded} icon="refresh" onClick={doLoadProcess}>
              Retry
            </Button>
          </Stack>
        </TableContentRow>
      )}
      {!loading && (
        <>
          {showChildRows && <TableChildRows rows={rowContent} onRowClick={onClick} expanded={expanded} even={even} />}
          {showNoRowsContent && (
            <TableContentRow even={even} width={viewportWidth} expanded={expanded} padding="small">
              {showNoRowsMessage && <Stack alignment="center">No items to display</Stack>}
            </TableContentRow>
          )}
          {showContent && (
            <TableContentRow even={even} width={viewportWidth} expanded={expanded}>
              {rowContent as ReactNode}
            </TableContentRow>
          )}
        </>
      )}
    </TableRowContext.Provider>
  );
};

/** The `TableBody.Row.Cell` component properties. */
export interface TableBodyRowCellProps extends BaseColumnProps {
  /** Indicates whether the cell is transitioning to/from something to the left. */
  readonly transitionLeft?: boolean;

  /** Indicates whether the cell is transitioning to/from something to the right. */
  readonly transitionRight?: boolean;

  /** The lock position. */
  readonly position?: number;

  /** The component children. */
  readonly children?: ReactNode;
}

/** Represents a single rendered cell within a table row. */
Row.Cell = (props: TableBodyRowCellProps) => {
  const { className, lock, align = "left", transitionLeft = false, transitionRight = false, position, children } = props;
  const { styles } = useTableStyles();

  const combinedClassName = classNames(
    styles.TableCell,
    "table-cell",
    `align-${align}`,
    lock !== undefined ? `lock-${lock}` : undefined,
    {
      "transition-left": transitionLeft,
      "transition-right": transitionRight,
    },
    className,
  );

  const style = useMemo((): CSSProperties => {
    if (position === undefined) return {};

    if (lock === "left") return { left: position };
    if (lock === "right") return { right: position };
    return {};
  }, [lock, position]);

  return (
    <td className={combinedClassName} style={style}>
      {lock === "left" && transitionRight && <div className="scroll-indicator left" />}
      {lock === "right" && transitionLeft && <div className="scroll-indicator right" />}
      <div className="cell-outer-content">
        <div className="cell-inner-content">{children}</div>
      </div>
    </td>
  );
};

/** Represents a single rendered row within the table. */
TableBody.Row = Row;
